import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';

const siteUrl = 'https://www.91short.com';

const milestones = [
  { year: '2022', title: '平台成立', description: '91short正式上线，专注短视频领域，首批创作者入驻平台。' },
  { year: '2023', title: '用户突破千万', description: '凭借优质内容和智能推荐，注册用户突破1000万，成为行业新星。' },
  { year: '2024', title: '全球化布局', description: '启动国际化战略，在东南亚、日韩等地区推出本地化版本。' },
  { year: '2025', title: '生态完善', description: '创作者变现体系全面升级，电商、直播、品牌合作多元发展。' },
  { year: '2026', title: '持续创新', description: 'AI创作工具上线，用户突破5000万，持续引领短视频行业创新。' },
];

const values = [
  { icon: 'ri-heart-line', title: '用户至上', description: '始终将用户体验放在首位，持续优化产品和服务，让每一位用户都能在91short找到属于自己的快乐。' },
  { icon: 'ri-lightbulb-flash-line', title: '创新驱动', description: '拥抱技术创新，不断探索短视频领域的前沿科技，为用户和创作者提供更强大的创作工具。' },
  { icon: 'ri-team-line', title: '合作共赢', description: '与创作者、品牌方共同成长，构建健康可持续的内容生态，实现多方价值的最大化。' },
  { icon: 'ri-shield-star-line', title: '责任担当', description: '坚持内容安全底线，积极履行社会责任，营造清朗健康的网络空间。' },
];

const stats = [
  { value: '5000万+', label: '月活跃用户' },
  { value: '200万+', label: '认证创作者' },
  { value: '10亿+', label: '短视频内容' },
  { value: '50+', label: '覆盖国家地区' },
];

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypotField = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotField && honeypotField.value.trim() !== '') {
      setFormStatus('success');
      return;
    }

    setFormStatus('loading');
    setFormError('');

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9pmh7mb4qjhggaeq170', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        setFormError(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后重试');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
      setFormStatus('error');
    }
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'AboutPage',
        name: '关于91short - 91short短视频平台',
        description: '了解91short的品牌故事、发展历程、核心价值观以及联系方式。',
        url: `${siteUrl}/about`,
        isPartOf: { '@type': 'WebSite', name: '91short', url: siteUrl },
      },
      {
        '@type': 'Organization',
        name: '91short短视频平台',
        url: siteUrl,
        description: '91short是专注于短视频内容创作与分享的平台。',
        foundingDate: '2022',
        contactPoint: {
          '@type': 'ContactPoint',
          contactType: 'customer service',
          email: 'contact@91short.com',
          availableLanguage: ['Chinese'],
        },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: '关于我们' },
        ],
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title="关于91short - 品牌故事、发展历程与联系方式 | 91short短视频平台"
        description="了解91short短视频平台的品牌故事、发展历程、核心价值观。91short致力于为全球用户提供优质的短视频创作与分享体验。联系我们获取更多信息。"
        keywords="91short,关于91short,91short品牌,91short联系方式,短视频平台"
        canonicalUrl={`${siteUrl}/about`}
        schemaData={aboutSchema}
      />

      <Navbar />

      <main className="pt-16">
        {/* Breadcrumb */}
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 flex-wrap" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-950 font-medium">关于我们</span>
          </nav>
        </div>

        {/* Hero */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
            <span className="inline-block px-4 py-1.5 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold mb-4">
              关于91short
            </span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-950 mb-6">
              用短视频连接每一个精彩瞬间
            </h1>
            <p className="text-foreground-600 text-base md:text-lg leading-relaxed">
              91short成立于2022年，是一家专注于短视频内容创作与分享的创新平台。我们汇聚了全球海量优质短视频资源，通过先进的AI推荐技术，为每一位用户提供个性化的内容体验。无论是记录日常生活、分享专业知识，还是展示创意才华，91short都是你最理想的舞台。
            </p>
          </div>
        </section>

        {/* Stats */}
        <section className="py-14 bg-background-100">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {stats.map((stat, i) => (
                <div key={i} className="text-center p-6">
                  <div className="text-3xl md:text-4xl font-heading font-bold text-primary-600 mb-2">{stat.value}</div>
                  <div className="text-sm text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Brand story & milestones */}
        <section className="py-20 md:py-28 bg-background-50">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <div className="text-center mb-16">
              <span className="inline-block px-4 py-1.5 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold mb-4">
                发展历程
              </span>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
                我们的成长之路
              </h2>
              <p className="text-foreground-600 text-base max-w-xl mx-auto">
                从初创到行业领先，每一步都脚踏实地
              </p>
            </div>

            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-200 md:-translate-x-px"></div>
              {milestones.map((milestone, index) => (
                <div
                  key={index}
                  className={`relative flex flex-col md:flex-row gap-8 mb-10 last:mb-0 ${
                    index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                >
                  <div className="md:w-1/2 flex items-start md:justify-end">
                    <div className={`ml-10 md:ml-0 md:mr-0 ${index % 2 === 0 ? 'md:pr-12 md:text-right' : 'md:pl-12'}`}>
                      {index % 2 === 0 && (
                        <div className="hidden md:block">
                          <span className="text-2xl font-heading font-bold text-primary-500">{milestone.year}</span>
                          <h3 className="text-lg font-heading font-semibold text-foreground-950 mt-1">{milestone.title}</h3>
                          <p className="text-sm text-foreground-600 mt-2">{milestone.description}</p>
                        </div>
                      )}
                      <div className="md:hidden">
                        <span className="text-lg font-heading font-bold text-primary-500">{milestone.year}</span>
                        <h3 className="text-base font-heading font-semibold text-foreground-950 mt-1">{milestone.title}</h3>
                        <p className="text-sm text-foreground-600 mt-2">{milestone.description}</p>
                      </div>
                    </div>
                  </div>
                  <div className="absolute left-4 md:left-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-primary-500 border-2 border-background-50 ring-2 ring-primary-200 z-10"></div>
                  <div className="md:w-1/2 flex items-start">
                    <div className={`ml-10 md:ml-0 ${index % 2 !== 0 ? 'md:pr-12 md:text-right' : 'md:pl-12'}`}>
                      {index % 2 !== 0 && (
                        <div className="hidden md:block">
                          <span className="text-2xl font-heading font-bold text-primary-500">{milestone.year}</span>
                          <h3 className="text-lg font-heading font-semibold text-foreground-950 mt-1">{milestone.title}</h3>
                          <p className="text-sm text-foreground-600 mt-2">{milestone.description}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="py-20 md:py-28 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-16">
              <span className="inline-block px-4 py-1.5 rounded-full bg-secondary-100 text-secondary-700 text-xs font-semibold mb-4">
                核心价值观
              </span>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
                我们的信念
              </h2>
              <p className="text-foreground-600 text-base max-w-xl mx-auto">
                这些价值观指引着我们每一天的工作
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {values.map((value, index) => (
                <div
                  key={index}
                  className="p-6 rounded-2xl bg-background-50 border border-background-200/70 hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="w-12 h-12 rounded-xl bg-accent-100 flex items-center justify-center mb-4">
                    <i className={`${value.icon} text-xl text-accent-600`}></i>
                  </div>
                  <h3 className="text-lg font-heading font-semibold text-foreground-950 mb-2">{value.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="py-20 md:py-28 bg-background-50">
          <div className="max-w-2xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-1.5 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold mb-4">
                联系我们
              </span>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
                期待与你交流
              </h2>
              <p className="text-foreground-600 text-base">
                有任何问题或合作意向，欢迎随时联系我们
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
              <div className="p-5 rounded-2xl bg-background-100 text-center">
                <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-mail-line text-lg text-primary-600"></i>
                </div>
                <h3 className="text-sm font-heading font-semibold text-foreground-950 mb-1">电子邮箱</h3>
                <p className="text-xs text-foreground-500 break-all">contact@91short.com</p>
              </div>
              <div className="p-5 rounded-2xl bg-background-100 text-center">
                <div className="w-10 h-10 rounded-xl bg-accent-100 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-phone-line text-lg text-accent-600"></i>
                </div>
                <h3 className="text-sm font-heading font-semibold text-foreground-950 mb-1">客服热线</h3>
                <p className="text-xs text-foreground-500">400-xxxx-xxxx</p>
              </div>
              <div className="p-5 rounded-2xl bg-background-100 text-center">
                <div className="w-10 h-10 rounded-xl bg-secondary-100 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-map-pin-line text-lg text-secondary-600"></i>
                </div>
                <h3 className="text-sm font-heading font-semibold text-foreground-950 mb-1">公司地址</h3>
                <p className="text-xs text-foreground-500">北京市朝阳区xxxx</p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5" data-readdy-form>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-2">姓名</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200/70 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-2">邮箱</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200/70 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                    placeholder="请输入您的邮箱"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-2">主题</label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  required
                  className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200/70 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                  placeholder="请输入咨询主题"
                />
              </div>
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-2">留言内容</label>
                <textarea
                  id="message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200/70 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                  placeholder="请输入您想咨询的内容（最多500字）"
                ></textarea>
                <p className="text-xs text-foreground-400 mt-1 text-right">最多500字</p>
              </div>

              {/* Anti-spam honeypot */}
              <div className="sp-unseen">
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>

              <button
                type="submit"
                disabled={formStatus === 'loading'}
                className="w-full py-3.5 rounded-xl bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                {formStatus === 'loading' ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>
                    提交中...
                  </span>
                ) : '提交留言'}
              </button>

              {formStatus === 'success' && (
                <div className="p-4 rounded-xl bg-accent-100 text-accent-800 text-sm text-center">
                  <i className="ri-check-line mr-1.5"></i>
                  感谢您的留言！我们会尽快与您联系。
                </div>
              )}
              {formStatus === 'error' && formError && (
                <div className="p-4 rounded-xl bg-red-50 text-red-700 text-sm text-center">
                  <i className="ri-error-warning-line mr-1.5"></i>
                  {formError}
                </div>
              )}
            </form>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}